// src/components/AnimatedSection.js
import React, { useEffect } from 'react'; // useEffect is used
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AnimatedSection = ({
    children,
    animationVariants, // For scroll-in/out animations based on visibility
    className,
    threshold = 0.1,
    triggerOnce = true, // For scroll-in/out behavior
    staggerChildren,    // For staggering children within animationVariants.visible
    delayChildren,      // For delaying children within animationVariants.visible
    tag = 'div',
    // --- Props for direct Framer Motion features like hover, tap, drag, etc. ---
    whileHover,
    transition,         // A general transition for this motion component (e.g., for hover)
    ...otherMotionProps // Captures any other valid motion props (e.g., layout, custom, onViewportEnter)
}) => {
    const controls = useAnimation();
    const { ref, inView } = useInView({
        triggerOnce: triggerOnce,
        threshold: threshold,
    });

    const MotionTag = motion[tag] || motion.div;

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        } else if (!triggerOnce) {
            controls.start('hidden');
        }
    }, [controls, inView, triggerOnce]);

    // Construct the variants to be used, ensuring stagger/delay are correctly merged
    // into the 'visible' state's transition.
    const baseVariants = animationVariants || { hidden: {}, visible: {} };
    const visibleState = baseVariants.visible || {};
    const visibleTransition = visibleState.transition || {};

    const finalVariants = {
        ...baseVariants,
        visible: {
            ...visibleState,
            transition: {
                ...visibleTransition,
                ...(staggerChildren && { staggerChildren }),
                ...(delayChildren && { delayChildren }),
            }
        }
    };

    return (
        <MotionTag
            ref={ref}
            className={className}
            // Props for scroll-triggered animation state
            initial="hidden"
            animate={controls}
            variants={finalVariants}
            // --- Crucial: Pass through hover and other direct motion props ---
            whileHover={whileHover}
            transition={transition} // This transition will apply to hover unless whileHover specifies its own.
                                    // Variants can also have their own transitions.
            {...otherMotionProps}   // Spread any other motion props passed in.
        >
            {children}
        </MotionTag>
    );
};

// --- Predefined Animation Variants (remain the same) ---
export const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

export const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut", delay: 0.2 }
    }
};

export const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.7, ease: "easeOut" }
    }
};

export const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.7, ease: "easeOut" }
    }
};

export const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

export const staggerItemFadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

export default AnimatedSection;